// backgroundAt — キャンバス背景色をログ全体から時点指定で求める pure ヘルパ
// (canvas-background-color-2026-06-20.md §8.5 の意味論そのまま、
// fix/timelapse-background 2026-09-27 Bug B/C 修正).
//
// 実際のキャンバスの正しい意味論 (staging 実データで確認済み、§8.5):
//   - 背景色は `whiteboard.background-set` で変わる。
//   - **全消し (`whiteboard.clear`、layerId の有無を問わない) では背景色は
//     変わらない** (サーバ snapshot も live クライアントも引き継ぐ)。
//   - **過去キャンバス復元 (`whiteboard.restore`) では背景色は復元元の背景色に
//     切り替わる** — ログには `background-set` は出ず、`restoredFromSeq` の
//     耐久 ID だけが残る。復元元時点の背景色は `backgroundAt(restoredFromSeq)`
//     を再帰的に辿ることで求まる (`restoredFromSeq` は必ずマーカーより前 =
//     停止性が保証される)。
//
// `timelapseReplay.ts` はこの関数を 2 箇所で使う:
//   - seed 開始時点 (= 最初に適用するイベントの直前) の初期背景。
//   - `resetForRestore` 時点 (= その restore マーカーの `restoredFromSeq`)。
//
// ログ開始前 (= 走査範囲に何も無い) の背景色は分からないため白 fallback
// ({@link DEFAULT_BACKGROUND}、drawing-core `EngineCore` の既定値と同じ)。
// `events` は昇順 seq (merge 済み) を前提とする。

import type { StrokeLogEvent } from '@oekaki/protocol';

/** キャンバス背景色 (broadcast 規約どおり alpha は常に不透明)。 */
export interface BackgroundColor {
  readonly rgb: number;
  readonly alpha: number;
}

/** ログに背景色の手がかりが無いときの fallback (= `EngineCore` の既定値)。 */
export const DEFAULT_BACKGROUND: BackgroundColor = { rgb: 0xffffff, alpha: 255 };

/**
 * `seq` 時点 (inclusive) でのキャンバス背景色を、ログ先頭から 1 回走査して
 * 求める。`whiteboard.restore` に当たったら `restoredFromSeq` (必ず自 seq より
 * 前 — 壊れたログの循環ガードとして `>=` は無視する) を再帰的に辿る。
 */
export const backgroundAt = (events: readonly StrokeLogEvent[], seq: number): BackgroundColor => {
  let current: BackgroundColor = DEFAULT_BACKGROUND;
  for (const ev of events) {
    if (ev.seq > seq) break; // ascending — nothing more at/before `seq`
    if (ev.t === 'whiteboard.background-set') {
      current = { rgb: ev.rgb, alpha: 255 };
    } else if (ev.t === 'whiteboard.restore' && ev.restoredFromSeq < ev.seq) {
      current = backgroundAt(events, ev.restoredFromSeq);
    }
    // whiteboard.clear (全消し) は背景色を変えない — current はそのまま。
  }
  return current;
};
